import { Injectable, inject } from '@angular/core';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { QueryCollection } from '@shared/models/query-collection';
import { HeaderService } from '@shared/services';
import { map, Observable, of, switchMap } from 'rxjs';
import {
  ActiveRuleRequest,
  ActiveRuleResponse,
  EntityEvaluationRequest,
  EvaluationRequest,
  EvaluationResponse,
  GstarClient,
  QuestionAnswerEvaluationRequest,
  SecurityEvaluationRequest,
} from 'src/app/web-api-client';
import { SecurityForm } from '../abstractions/security-form';
import { RulesForm } from '../models/rules-form';

@Injectable()
export class TaxRulesService {
  private readonly gstarClient = inject(GstarClient);
  private readonly headerService = inject(HeaderService);

  loadDynamicForm(
    formValue: Partial<SecurityForm>
  ): Observable<QueryCollection<RulesForm>> {
    const request = this.getActiveRuleRequest(formValue);

    return this.getActiveRules(request).pipe(
      switchMap(
        (
          queryCollection: QueryCollection<RulesForm>
        ): Observable<QueryCollection<RulesForm>> => {
          if (!queryCollection.any()) {
            return of(queryCollection);
          }
          return this.firstExecution(queryCollection, formValue);
        }
      )
    );
  }

  evaluate(
    ruleForm: RulesForm,
    formValue: Partial<SecurityForm>,
    questions: QuestionAnswerEvaluationRequest[]
  ): Observable<RulesForm> {
    const evaluationsRequest: EvaluationRequest = this.getEvaluationRequest(
      ruleForm.activeRule!,
      formValue,
      questions
    );

    return this.execute(evaluationsRequest).pipe(
      map((response: EvaluationResponse) => {
        ruleForm.data = response;
        return ruleForm;
      })
    );
  }

  execute(request: EvaluationRequest): Observable<EvaluationResponse> {
    return this.gstarClient.execute(request);
  }

  private getActiveRules(
    request: ActiveRuleRequest
  ): Observable<QueryCollection<RulesForm>> {
    return this.gstarClient
      .activerules(request)
      .pipe(
        map(
          (activeRules: ActiveRuleResponse[]) =>
            new QueryCollection(
              activeRules.map(
                (activeRule: ActiveRuleResponse) => new RulesForm(activeRule)
              )
            )
        )
      );
  }

  private firstExecution(
    queryCollection: QueryCollection<RulesForm>,
    formValue: Partial<SecurityForm>
  ): Observable<QueryCollection<RulesForm>> {
    const evaluationsRequest: EvaluationRequest = this.getEvaluationRequest(
      queryCollection.first.activeRule!,
      formValue
    );

    return this.execute(evaluationsRequest).pipe(
      map((response: EvaluationResponse) => {
        queryCollection.first.data = response;
        return queryCollection;
      })
    );
  }

  private getActiveRuleRequest(
    formValue: Partial<SecurityForm>
  ): ActiveRuleRequest {
    const isGeneralRates =
      formValue.transactionType === TransactionType.GeneralRates;

    const clientId =
      formValue.clientId ?? this.headerService.selectedClient()?.id;
    const generalRateEntityTypeId = isGeneralRates
      ? (formValue.generalRateEntityTypeId ?? undefined)
      : undefined;

    return new ActiveRuleRequest({
      entityTypeId: isGeneralRates
        ? (generalRateEntityTypeId ?? undefined)
        : formValue.entity?.entityTypeId,
      residencyJurisdictionId: isGeneralRates
        ? undefined
        : formValue.entity?.residencyJurisdictionId,
      investmentJurisdictionId:
        formValue.investmentJurisdiction?.investmentJurisdictionId,
      securityTypeId: formValue.securityType?.securityTypeId,
      generalRateEntityTypeId,
      generalRate: isGeneralRates,
      clientID: clientId,
    });
  }

  private getEvaluationRequest(
    activeRule: ActiveRuleResponse,
    formValue: Partial<SecurityForm>,
    questions: QuestionAnswerEvaluationRequest[] = []
  ): EvaluationRequest {
    const entity = new EntityEvaluationRequest();
    const security = new SecurityEvaluationRequest();
    const isGeneralRates =
      formValue.transactionType === TransactionType.GeneralRates;
    const clientId =
      formValue.clientId ?? this.headerService.selectedClient()?.id;

    if (isGeneralRates) {
      entity.entityId = 0;
    } else if (formValue.entity) {
      entity.entityId = formValue.entity.entityId;
    }

    security.transactionTypeId = formValue.transactionType;
    security.acquisitionDate = formValue.acquisitionDate
      ? (formValue.acquisitionDate as Date[])[0]
      : undefined;

    return new EvaluationRequest({
      generalRateEntityTypeId: isGeneralRates
        ? (formValue.generalRateEntityTypeId ?? undefined)
        : undefined,
      clientId: isGeneralRates ? clientId : undefined,
      taxPolicyId: activeRule.taxPolicyId,
      taxRuleId: activeRule.taxRuleId,
      processedAnswers: questions.slice(0, -1),
      lastAnswer: questions[questions.length - 1],
      entity,
      security,
    });
  }
}
